<app-layout>
  <div class="report-designer-page">
    <header class="page-header">
      <div class="title-block">
        <span class="eyebrow" appLocalizationPopupDirective localizationCode="ReportDesigner">{{ 'ReportDesigner' | translate: {Default: "Report Designer"} }}</span>
        <div class="title-row">
          <h1 *ngIf="reportDisplayName; else titleSkeleton">{{ reportDisplayName }}</h1>
          <ng-template #titleSkeleton>
            <ngx-skeleton-loader *ngIf="loadingReport; else untitled" class="title-skeleton" count="1" [theme]="{ 'height.px': 30, 'width.px': 260, 'border-radius': '8px', 'margin-bottom': '0' }"></ngx-skeleton-loader>
            <ng-template #untitled><h1>{{ 'UntitledReport' | translate: {Default: "Untitled report"} }}</h1></ng-template>
          </ng-template>

          <span *ngIf="statusName" class="status-badge"
            [class.draft]="statusName === 'Draft'"
            [class.published]="statusName === 'Published'"
            [class.archived]="statusName === 'Archived'">
            <span class="dot"></span>{{ statusName }}
          </span>
        </div>
        <div class="meta-row" *ngIf="report?.type?.name || report?.reportDataSource?.name">
          <span *ngIf="report?.type?.name" class="meta-item">
            <mat-icon class="material-icons-outlined" aria-hidden="true">category</mat-icon>{{ report.type.name }}
          </span>
          <span *ngIf="report?.reportDataSource?.name" class="meta-item">
            <mat-icon class="material-icons-outlined" aria-hidden="true">storage</mat-icon>{{ report.reportDataSource.name }}
          </span>
        </div>
      </div>

      <div class="header-actions">
        <button class="rounded-button" mat-stroked-button appPreventDoubleClick (throttledClick)="goToAdmin()">
          {{ 'Close' | translate: {Default: "Close"} }}
        </button>
      </div>
    </header>

    <div *ngIf="errorMessage" class="report-designer-error">
      <mat-icon class="material-icons-outlined" aria-hidden="true">error_outline</mat-icon>
      <span>{{ errorMessage }}</span>
    </div>

    <div class="main-content">
      <aside class="settings-column" *ngIf="reportId">
        <section class="surface-card settings-card">
          <header class="card-header">
            <div class="card-icon">
              <mat-icon class="material-icons-outlined" aria-hidden="true">settings</mat-icon>
            </div>
            <div class="card-copy">
              <h2 appLocalizationPopupDirective localizationCode="ReportSettings">{{ 'ReportSettings' | translate: {Default: "Report settings"} }}</h2>
              <p>{{ 'ReportSettingsHint' | translate: {Default: "Describe the report, choose who can see it, then publish."} }}</p>
            </div>
          </header>

          <div class="settings-tabs" role="tablist" [attr.aria-label]="'ReportSettings' | translate: {Default: 'Report settings'}">
            <button type="button" role="tab" class="settings-tab" id="report-tab-details"
              aria-controls="report-panel-details"
              [attr.aria-selected]="activeSettingsTab === 'details'"
              [class.active]="activeSettingsTab === 'details'"
              (click)="activeSettingsTab = 'details'">
              <mat-icon class="material-icons-outlined" aria-hidden="true">description</mat-icon>
              <span>{{ 'ReportDetails' | translate: {Default: "Details"} }}</span>
              <span class="unsaved-dot" *ngIf="!detailsFormPristine" [matTooltip]="'UnsavedChanges' | translate: {Default: 'Unsaved changes'}"></span>
            </button>

            <button type="button" role="tab" class="settings-tab" id="report-tab-access"
              aria-controls="report-panel-access"
              [attr.aria-selected]="activeSettingsTab === 'access'"
              [class.active]="activeSettingsTab === 'access'"
              (click)="activeSettingsTab = 'access'">
              <mat-icon class="material-icons-outlined" aria-hidden="true">lock</mat-icon>
              <span>{{ 'ReportAccess' | translate: {Default: "Access"} }}</span>
              <span class="count" *ngIf="assignedRoleCount !== null">{{ assignedRoleCount }}</span>
              <span class="unsaved-dot" *ngIf="securityDirty" [matTooltip]="'UnsavedChanges' | translate: {Default: 'Unsaved changes'}"></span>
            </button>

            <button type="button" role="tab" class="settings-tab" id="report-tab-status"
              aria-controls="report-panel-status"
              [attr.aria-selected]="activeSettingsTab === 'status'"
              [class.active]="activeSettingsTab === 'status'"
              (click)="activeSettingsTab = 'status'">
              <mat-icon class="material-icons-outlined" aria-hidden="true">published_with_changes</mat-icon>
              <span>{{ 'ReportStatus' | translate: {Default: "Status"} }}</span>
              <span class="unsaved-dot" *ngIf="!statusFormPristine" [matTooltip]="'UnsavedChanges' | translate: {Default: 'Unsaved changes'}"></span>
            </button>
          </div>

          <!-- Panels are hidden rather than removed so unsaved edits survive switching tabs. -->
          <div class="tab-panels">
            <div class="tab-panel" role="tabpanel" id="report-panel-details" aria-labelledby="report-tab-details" [hidden]="activeSettingsTab !== 'details'">
              <p class="panel-hint">{{ 'ReportDetailsHint' | translate: {Default: "Name, description, type and data source for this report."} }}</p>

              <ng-container *ngIf="loadingReport || !report; else detailsForm">
                <ngx-skeleton-loader count="4" [theme]="{ 'height.px': 52, 'border-radius': '10px' }"></ngx-skeleton-loader>
              </ng-container>
              <ng-template #detailsForm>
                <app-form-generator
                  [formId]="detailsFormId"
                  [formData]="detailsFormData"
                  [emitFormDataTrigger]="getDetailsFormData"
                  (emitFormData)="saveDetails($event)"
                  (emitFormPristine)="detailsFormPristine = $event"
                  (emitFormStatus)="detailsFormValid = $event"
                ></app-form-generator>
              </ng-template>

              <footer class="card-footer">
                <span class="save-state" *ngIf="!detailsFormPristine">
                  <span class="dot"></span>
                  {{ 'UnsavedChanges' | translate: {Default: "Unsaved changes"} }}
                </span>
                <button class="rounded-button" mat-flat-button setColor="primaryColour" color="primary"
                  [disabled]="loadingReport || savingDetails || !detailsFormValid || detailsFormPristine"
                  appPreventDoubleClick (throttledClick)="getDetailsFormData = !getDetailsFormData">
                  {{ 'SaveDetails' | translate: {Default: "Save details"} }}
                </button>
              </footer>
            </div>

            <div class="tab-panel" role="tabpanel" id="report-panel-access" aria-labelledby="report-tab-access" [hidden]="activeSettingsTab !== 'access'">
              <p class="panel-hint">{{ 'ReportAccessHint' | translate: {Default: "Choose which security roles can view this report."} }}</p>

              <app-report-security-panel
                [reportId]="reportId"
                (dirtyChange)="securityDirty = $event"
                (assignedCountChange)="assignedRoleCount = $event"
              ></app-report-security-panel>
            </div>

            <div class="tab-panel" role="tabpanel" id="report-panel-status" aria-labelledby="report-tab-status" [hidden]="activeSettingsTab !== 'status'">
              <p class="panel-hint">{{ 'ReportStatusHint' | translate: {Default: "Only published reports are available to users."} }}</p>

              <ng-container *ngIf="loadingReport || !report; else statusForm">
                <ngx-skeleton-loader count="1" [theme]="{ 'height.px': 52, 'border-radius': '10px' }"></ngx-skeleton-loader>
              </ng-container>
              <ng-template #statusForm>
                <app-form-generator
                  [formId]="statusFormId"
                  [formData]="statusFormData"
                  [emitFormDataTrigger]="getStatusFormData"
                  [skeletonLoadingRows]="1"
                  (emitFormData)="saveStatus($event)"
                  (emitFormPristine)="statusFormPristine = $event"
                  (emitFormStatus)="statusFormValid = $event"
                ></app-form-generator>
              </ng-template>

              <footer class="card-footer">
                <span class="save-state" *ngIf="!statusFormPristine">
                  <span class="dot"></span>
                  {{ 'UnsavedChanges' | translate: {Default: "Unsaved changes"} }}
                </span>
                <button class="rounded-button" mat-flat-button setColor="primaryColour" color="primary"
                  [disabled]="loadingReport || savingStatus || !statusFormValid || statusFormPristine"
                  appPreventDoubleClick (throttledClick)="getStatusFormData = !getStatusFormData">
                  {{ 'UpdateStatus' | translate: {Default: "Update status"} }}
                </button>
              </footer>
            </div>
          </div>
        </section>
      </aside>

      <!-- Layout designer. Always rendered - the Telerik widget binds to #webReportDesigner by id. -->
      <section class="surface-card designer-card">
        <header class="card-header">
          <div class="card-icon">
            <mat-icon class="material-icons-outlined" aria-hidden="true">dashboard_customize</mat-icon>
          </div>
          <div class="card-copy">
            <h2 appLocalizationPopupDirective localizationCode="ReportLayout">{{ 'ReportLayout' | translate: {Default: "Report layout"} }}</h2>
            <p>{{ 'ReportLayoutHint' | translate: {Default: "Design the report and save it from the designer toolbar."} }}</p>
          </div>
          <span class="save-state layout-save-state" *ngIf="layoutDirty">
            <span class="dot"></span>
            {{ 'UnsavedLayoutChanges' | translate: {Default: "Unsaved layout changes"} }}
          </span>
        </header>

        <div class="report-designer-container" #designerContainer>
          <div id="webReportDesigner" class="report-designer-root"></div>
        </div>
      </section>
    </div>
  </div>
</app-layout>
